import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { ClientContact, ClientContactBody, ClientDetail } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Dialog, EmptyState } from '@/components/ui/misc';
import { errorMessage } from '@/features/clients/clients-queries';
import { RowIconButton } from '@/features/clients/client-addresses-section';

export function ClientContactsSection({ client }: { client: ClientDetail }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ contact: ClientContact | null } | null>(null);

  const remove = useMutation({
    mutationFn: async (contactId: number) => {
      const res = await api<void>(`/clients/${client.id}/contacts/${contactId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] }),
  });

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <h2 className="text-base font-semibold">Contatos</h2>
        <Button size="sm" onClick={() => setDialog({ contact: null })}>
          <Plus className="size-4" strokeWidth={2.5} />
          Contato
        </Button>
      </div>
      {client.contacts.length === 0 ? (
        <EmptyState className="h-28" message="Nenhum contato cadastrado" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">E-mail</th>
                <th className="h-9 px-4 font-semibold">Telefone</th>
                <th className="h-9 px-4 font-semibold">Tipo de contato</th>
                <th className="h-9 w-20 px-4">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {client.contacts.map((c) => (
                <tr
                  key={c.id}
                  className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                >
                  <td className="max-w-44 truncate px-4 font-medium">{c.owner ?? '—'}</td>
                  <td className="max-w-56 truncate px-4 text-muted-foreground">
                    {c.email ?? '—'}
                  </td>
                  <td className="px-4 whitespace-nowrap">
                    {c.telephone ? (
                      <a
                        href={`tel:${c.telephone.replace(/[^\d+]/g, '')}`}
                        className="text-primary tabular-nums hover:text-primary-pressed hover:underline"
                      >
                        {c.telephone}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 whitespace-nowrap text-muted-foreground">
                    {c.use === 'personal' ? 'Pessoal' : 'Comercial'}
                  </td>
                  <td className="px-4">
                    <div className="flex justify-end gap-0.5">
                      <RowIconButton label="Editar contato" onClick={() => setDialog({ contact: c })}>
                        <Pencil className="size-3.5" />
                      </RowIconButton>
                      <RowIconButton
                        label="Remover contato"
                        destructive
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm('Remover este contato?')) remove.mutate(c.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </RowIconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.error ? (
        <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}
      {dialog ? (
        <ContactDialog
          clientId={client.id}
          contact={dialog.contact}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  );
}

function ContactDialog({
  clientId,
  contact,
  onClose,
}: {
  clientId: number;
  contact: ClientContact | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [owner, setOwner] = useState(contact?.owner ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [telephone, setTelephone] = useState(contact?.telephone ?? '');
  const [use, setUse] = useState<'personal' | 'commercial'>(contact?.use ?? 'commercial');

  const save = useMutation({
    mutationFn: async (body: ClientContactBody) => {
      const res = contact
        ? await api<ClientContact>(`/clients/${clientId}/contacts/${contact.id}`, {
            method: 'PUT',
            body,
          })
        : await api<ClientContact>(`/clients/${clientId}/contacts`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      owner: owner.trim() || null,
      email: email.trim() || null,
      telephone: telephone.trim() || null,
      use,
    });
  };

  return (
    <Dialog open onClose={onClose} title={contact ? 'Editar contato' : 'Novo contato'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="contact-owner">Nome</Label>
          <Input id="contact-owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contact-email">E-mail</Label>
          <Input
            id="contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contact-telephone">Telefone</Label>
          <Input
            id="contact-telephone"
            type="tel"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contact-use">Tipo de contato</Label>
          <Select
            id="contact-use"
            value={use}
            onChange={(e) => setUse(e.target.value === 'personal' ? 'personal' : 'commercial')}
          >
            <option value="commercial">Comercial</option>
            <option value="personal">Pessoal</option>
          </Select>
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
